// @flow

import ApiUser from 'PMT/api/ApiUser';
import ApiError from 'PMT/entities/ApiError';
import I18n from 'PMT/i18n/I18n';
import SessionUtils from 'PMT/utils/SessionUtils';
import Config from 'constants/Config';
import Constants from 'constants/Constants';

const REFRESH_MARGIN = 60 * 1000;

function sessionError(): ApiError {
  const error = new ApiError(0, 'The pro session is no longer valid');
  error.localizedMessage = I18n.tr('api.error.pro.oauth.session_expired');
  return error;
}

function isStoredToken(token: ?Object): boolean {
  return !!token
      && typeof token.access_token === 'string' && token.access_token.length > 0
      && typeof token.refresh_token === 'string' && token.refresh_token.length
      > 0
      && Number.isFinite(token.expiresAt)
      && typeof token.clientId === 'string' && token.clientId.length > 0;
}

class ProAuth {
  constructor() {
    const token = SessionUtils.getItemFromSession(
        Constants.SessionKey.PRO_OAUTH);
    this.token = isStoredToken(token) ? token : null;
    this.authorizations = new Set();
    this.sessionVersion = 0;
    this.clientPromise = null;
    this.refreshPromise = null;
    this.onSessionExpired = null;
    this.onError = null;
    if (this.token) {
      this.authorizations.add(`Bearer ${this.token.access_token}`);
    }
  }

  getAuthorization(): ?string {
    if (!this.token) {
      return null;
    }
    return `Bearer ${this.token.access_token}`;
  }

  clear() {
    this.sessionVersion += 1;
    this.token = null;
    this.authorizations.clear();
    this.refreshPromise = null;
    localStorage.removeItem(Constants.SessionKey.PRO_OAUTH);
    localStorage.removeItem(Constants.SessionKey.AUTHORIZATION);
  }

  expire() {
    this.clear();
    if (this.onSessionExpired) {
      this.onSessionExpired();
    }
  }

  getClient(): Promise<Object> {
    if (!this.clientPromise) {
      this.clientPromise = new Promise(
          (resolve: Function, reject: Function) => {
            ApiUser.getProOAuthClient(Config.apikey, resolve, reject);
          }).catch((error: ApiError) => {
        this.clientPromise = null;
        throw error;
      });
    }
    return this.clientPromise;
  }

  saveToken(response: Object, client: Object, version: number): Object {
    if (version !== this.sessionVersion) {
      throw sessionError();
    }
    const expiresIn = response
    && (typeof response.expires_in === 'number' || typeof response.expires_in
        === 'string')
        ? Number(response.expires_in) : NaN;
    if (!response
        || typeof response.access_token !== 'string' || !response.access_token
        || typeof response.refresh_token !== 'string' || !response.refresh_token
        || !Number.isFinite(expiresIn) || expiresIn <= 0
        || (response.token_type
            && (typeof response.token_type !== 'string'
                || response.token_type.toLowerCase() !== 'bearer'))
        || !Number.isFinite(Date.now() + expiresIn)) {
      const error = new ApiError(0, 'Invalid pro OAuth token response');
      error.localizedMessage = I18n.tr('api.error.pro.oauth.invalid_token');
      throw error;
    }
    const token = {
      access_token: response.access_token,
      refresh_token: response.refresh_token,
      expiresAt: Date.now() + expiresIn,
      clientId: client.clientId,
    };
    SessionUtils.saveObjectToSession(Constants.SessionKey.PRO_OAUTH, token);
    this.token = token;
    this.authorizations.add(`Bearer ${token.access_token}`);
    return token;
  }

  login(username: string, password: string): Promise<Object> {
    this.clear();
    const version = this.sessionVersion;
    return this.getClient().then((client: Object) => {
      if (version !== this.sessionVersion) {
        throw sessionError();
      }
      return new Promise((resolve: Function, reject: Function) => {
        ApiUser.loginPro(username, password, Config.apikey, client, resolve,
            reject);
      }).then((response: Object) => this.saveToken(response, client, version));
    });
  }

  refresh(): Promise<Object> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    const {token} = this;
    const version = this.sessionVersion;
    if (!token || !this.getAuthorization()) {
      return Promise.reject(sessionError());
    }
    const pending = this.getClient().then((client: Object) => {
      if (version !== this.sessionVersion) {
        throw sessionError();
      }
      if (client.clientId !== token.clientId) {
        this.expire();
        throw sessionError();
      }
      return new Promise((resolve: Function, reject: Function) => {
        ApiUser.refreshProToken(token.refresh_token, Config.apikey, client,
            resolve, reject);
      }).then((response: Object) => this.saveToken(response, client, version));
    }).catch((error: ApiError) => {
      if (version === this.sessionVersion
          && (error.statusCode === 401 || error.statusCode === 403
              || (error.statusCode === 400
                  && (error.oauthError === 'invalid_grant' || String(error.code)
                      === '1015')))) {
        const expiredError = sessionError();
        expiredError.statusCode = error.statusCode;
        expiredError.oauthError = error.oauthError;
        this.expire();
        throw expiredError;
      }
      throw error;
    });
    this.refreshPromise = pending;
    const clearPending = () => {
      if (this.refreshPromise === pending) {
        this.refreshPromise = null;
      }
    };
    pending.then(clearPending, clearPending);
    return pending;
  }

  prepareRequest(send: Function, failure: Function) {
    const version = this.sessionVersion;
    if (!this.getAuthorization()) {
      send();
      return;
    }
    this.getClient().then((client: Object) => {
      if (version !== this.sessionVersion || !this.token) {
        throw sessionError();
      }
      if (client.clientId !== this.token.clientId) {
        this.expire();
        throw sessionError();
      }
      if (this.refreshPromise || this.token.expiresAt - Date.now()
          <= REFRESH_MARGIN) {
        return this.refresh();
      }
      return this.token;
    }).then(() => {
      if (version !== this.sessionVersion) {
        failure(sessionError());
        return;
      }
      send();
    }, (error: ApiError) => {
      this.reportError(error);
      failure(error);
    });
  }

  handleResponse(error: ?Object, response: ?Object, context: Object): boolean {
    const {
      authorization, retried, retry, failure,
    } = context;
    if (!authorization || !authorization.startsWith('Bearer ')) {
      return false;
    }
    if (!this.authorizations.has(authorization) || !this.getAuthorization()) {
      failure(sessionError());
      return true;
    }
    if (!response || response.statusCode !== 401) {
      return false;
    }
    if (retried) {
      if (authorization === this.getAuthorization()) {
        this.expire();
      }
      failure(sessionError());
      return true;
    }
    if (authorization !== this.getAuthorization()) {
      retry();
      return true;
    }
    const version = this.sessionVersion;
    this.refresh().then(() => {
      if (version !== this.sessionVersion) {
        failure(sessionError());
        return;
      }
      retry();
    }, (refreshError: ApiError) => {
      this.reportError(refreshError);
      failure(refreshError);
    });
    return true;
  }

  reportError(error: ApiError) {
    console.error('[OAuth pro]', error.statusCode || error.code, error.message);
    if (this.onError) {
      this.onError(error);
    }
  }
}

export default new ProAuth();
